import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconLink } from '../../components/ui/EditIconLink';
import { EnrichIconButton } from '../../components/ui/EnrichIconButton';
import { RoundedCheckbox } from '../../components/ui/RoundedCheckbox';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { listCampaignTags, ProspectCampaignTag } from '../../services/campaignTagsApi';
import {
  deleteProspect,
  enrichProspect,
  enrichProspectsBatch,
  listProspects,
  markProspectLost,
  permanentDeleteProspect,
  Prospect,
  ProspectListFilter,
} from '../../services/prospectsApi';

const ENRICHMENT_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Pending', label: 'Pendente' },
  { value: 'InProgress', label: 'Em progresso' },
  { value: 'Completed', label: 'Concluído' },
  { value: 'Failed', label: 'Falhou' },
  { value: 'Skipped', label: 'Ignorado' },
];

const ENRICHMENT_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  InProgress: 'Em progresso',
  Completed: 'Concluído',
  Failed: 'Falhou',
  Skipped: 'Ignorado',
};

const FUNNEL_LABELS: Record<string, string> = {
  Captured: 'Capturado',
  Lead: 'Lead',
  Lost: 'Perdido',
};

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
}

function enrichmentLabel(status: string): string {
  return ENRICHMENT_LABELS[status] ?? status;
}

function funnelLabel(stage: string): string {
  return FUNNEL_LABELS[stage] ?? stage;
}

type ProspectsPageProps = {
  variant?: 'all' | 'leads';
};

export function ProspectsPage({ variant = 'all' }: ProspectsPageProps) {
  const leadsOnly = variant === 'leads';
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.prospects');

  const [items, setItems] = useState<Prospect[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [campaignTags, setCampaignTags] = useState<ProspectCampaignTag[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [enriching, setEnriching] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [enrichmentStatus, setEnrichmentStatus] = useState('');
  const [campaignTag, setCampaignTag] = useState('');
  const [hasEmail, setHasEmail] = useState('');
  const [captureSource, setCaptureSource] = useState('');

  async function loadProspects(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const filter: ProspectListFilter = {
        page: targetPage,
        pageSize,
      };
      if (companyName.trim()) filter.companyName = companyName.trim();
      if (title.trim()) filter.title = title.trim();
      if (enrichmentStatus) filter.enrichmentStatus = enrichmentStatus;
      if (campaignTag) filter.campaignTag = campaignTag;
      if (hasEmail === 'yes') filter.hasEmail = true;
      if (hasEmail === 'no') filter.hasEmail = false;
      if (captureSource.trim()) filter.captureSource = captureSource.trim();
      filter.funnelStage = leadsOnly ? 'Lead,Lost' : 'Captured';

      const result = await listProspects(filter);
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
      setSelectedIds(new Set());
    } catch {
      toast.error(leadsOnly ? 'Falha ao carregar leads.' : 'Falha ao carregar prospects.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void listCampaignTags({ page: 1, pageSize: 200 })
      .then((result) => setCampaignTags(result.items))
      .catch(() => toast.error('Falha ao carregar campanhas.'));
  }, []);

  useEffect(() => {
    void loadProspects(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function handleFilterSubmit(event: FormEvent): void {
    event.preventDefault();
    setPage(1);
    void loadProspects(1);
  }

  function toggleSelect(id: string): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll(): void {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  }

  async function handleEnrichOne(id: string): Promise<void> {
    if (!canWrite) return;
    try {
      await enrichProspect(id);
      toast.success('Enriquecimento enfileirado.');
      await loadProspects(page);
    } catch {
      toast.error('Falha ao enfileirar enriquecimento.');
    }
  }

  async function handleDeleteProspect(prospect: Prospect): Promise<void> {
    if (!canWrite) return;

    if (prospect.actions.canSoftDelete) {
      const message =
        prospect.funnelStage === 'Lead'
          ? 'Remover este lead da base (exclusão lógica)?'
          : 'Excluir este prospect?';
      if (!await confirm({
        title: 'Remover prospect',
        message,
        confirmLabel: 'Remover',
        tone: 'danger',
      })) {
        return;
      }
      try {
        await deleteProspect(prospect.id);
        toast.success('Prospect removido.');
        await loadProspects(page);
      } catch {
        toast.error('Falha ao remover prospect.');
      }
      return;
    }

    if (!prospect.actions.canPermanentDelete) return;
    if (
      !await confirm({
        title: 'Excluir definitivamente',
        message: 'Excluir definitivamente este registro? Esta ação não pode ser desfeita.',
        confirmLabel: 'Excluir',
        tone: 'danger',
      })
    ) {
      return;
    }
    try {
      await permanentDeleteProspect(prospect.id);
      toast.success('Registro excluído definitivamente.');
      await loadProspects(page);
    } catch {
      toast.error('Falha ao excluir definitivamente.');
    }
  }

  async function handleDeleteBatch(): Promise<void> {
    if (!canWrite || selectedIds.size === 0) return;

    const targets = items.filter(
      (prospect) =>
        selectedIds.has(prospect.id) &&
        (prospect.actions.canSoftDelete || prospect.actions.canPermanentDelete),
    );

    if (targets.length === 0) {
      toast.error('Nenhum dos selecionados pode ser excluído.');
      return;
    }

    if (
      !await confirm({
        title: 'Excluir selecionados',
        message: `Excluir ${targets.length} prospect(s) selecionado(s)?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      })
    ) {
      return;
    }

    setDeleting(true);
    let successCount = 0;
    let failedCount = 0;

    for (const prospect of targets) {
      try {
        if (prospect.actions.canSoftDelete) {
          await deleteProspect(prospect.id);
        } else {
          await permanentDeleteProspect(prospect.id);
        }
        successCount += 1;
      } catch {
        failedCount += 1;
      }
    }

    setDeleting(false);

    if (successCount > 0) {
      toast.success(`${successCount} prospect(s) excluído(s).`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} falha(s) ao excluir.`);
    }

    await loadProspects(page);
  }

  async function handleMarkLost(id: string): Promise<void> {
    if (
      !canWrite ||
      !(await confirm({
        title: 'Marcar como perdido',
        message: 'Marcar este lead como perdido?',
        confirmLabel: 'Marcar como perdido',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await markProspectLost(id);
      toast.success('Lead marcado como perdido.');
      await loadProspects(page);
    } catch {
      toast.error('Falha ao marcar como perdido.');
    }
  }

  async function handleEnrichBatch(): Promise<void> {
    if (!canWrite || selectedIds.size === 0) return;
    setEnriching(true);
    try {
      const result = await enrichProspectsBatch(Array.from(selectedIds));
      toast.success(`${result.queuedCount} prospect(s) enfileirado(s) para enriquecimento.`);
      await loadProspects(page);
    } catch {
      toast.error('Falha ao enfileirar enriquecimento em lote.');
    } finally {
      setEnriching(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageShell>
      <PageHeader
        title={leadsOnly ? 'Leads' : 'Prospects'}
        description={
          leadsOnly
            ? 'Contatos prontos para a abordagem comercial. O próximo passo é abrir uma oportunidade.'
            : 'Pessoas e empresas em prospecção, para descobrir quem pode comprar.'
        }
        actions={
          canWrite ? (
            <Link
              to={leadsOnly ? '/sales/leads/new' : '/sales/prospects/new'}
              className="erp-btn-primary"
            >
              {leadsOnly ? 'Novo lead' : 'Novo prospect'}
            </Link>
          ) : undefined
        }
      />

      <form onSubmit={handleFilterSubmit} className="erp-filter-grid">
        <label className="erp-label">
          Empresa
          <input
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
          />
        </label>
        <label className="erp-label">
          Cargo
          <input
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label className="erp-label">
          Campanha
          <select
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            value={campaignTag}
            onChange={(event) => setCampaignTag(event.target.value)}
          >
            <option value="">Todas</option>
            {campaignTags.map((tag) => (
              <option key={tag.id} value={tag.name}>
                {tag.name}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label">
          Enriquecimento
          <select
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            value={enrichmentStatus}
            onChange={(event) => setEnrichmentStatus(event.target.value)}
          >
            {ENRICHMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label">
          E-mail
          <select
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            value={hasEmail}
            onChange={(event) => setHasEmail(event.target.value)}
          >
            <option value="">Todos</option>
            <option value="yes">Com e-mail</option>
            <option value="no">Sem e-mail</option>
          </select>
        </label>
        <label className="erp-label">
          Origem
          <input
            className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2"
            placeholder="Ex.: chrome-extension"
            value={captureSource}
            onChange={(event) => setCaptureSource(event.target.value)}
          />
        </label>
        <div className="md:col-span-3 lg:col-span-6 erp-btn-actions">
          <button type="submit" className="erp-btn-secondary">
            Filtrar
          </button>
          {canWrite && selectedIds.size > 0 ? (
            <>
              <button
                type="button"
                disabled={enriching}
                className="erp-btn-integration"
                onClick={() => void handleEnrichBatch()}
              >
                Enriquecer selecionados ({selectedIds.size})
              </button>
              <button
                type="button"
                disabled={deleting}
                className="erp-btn-danger"
                onClick={() => void handleDeleteBatch()}
              >
                Excluir selecionados ({selectedIds.size})
              </button>
            </>
          ) : null}
        </div>
      </form>

      <div className="erp-table-wrap">
        {loading ? (
          <p className="erp-loading">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="erp-empty">
            {leadsOnly ? 'Nenhum lead encontrado.' : 'Nenhum prospect encontrado.'}
          </p>
        ) : (
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  {canWrite ? (
                    <th className="px-4 py-3 w-10">
                      <RoundedCheckbox
                        checked={items.length > 0 && selectedIds.size === items.length}
                        onChange={toggleSelectAll}
                        aria-label="Selecionar todos"
                      />
                    </th>
                  ) : null}
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Campanha</th>
                  <th className="px-4 py-3">Enriquecimento</th>
                  {leadsOnly ? <th className="px-4 py-3">E-mail</th> : <th className="px-4 py-3">Funil</th>}
                  <th className="px-4 py-3">Capturado em</th>
                  <th className="px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((prospect) => (
                  <tr key={prospect.id}>
                    {canWrite ? (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(prospect.id)}
                          onChange={() => toggleSelect(prospect.id)}
                          aria-label={`Selecionar ${prospect.fullName}`}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <div className="font-medium text-secondary-900">{prospect.fullName}</div>
                      {prospect.title ? (
                        <div className="text-xs text-secondary-500">{prospect.title}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-secondary-700">{prospect.companyName ?? '—'}</td>
                    <td className="px-4 py-3 text-secondary-700">{prospect.campaignTag ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs">
                        {enrichmentLabel(prospect.enrichmentStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {leadsOnly ? (
                        <span className="text-secondary-700">
                          {prospect.email ?? '—'}
                          {prospect.funnelStage === 'Lost' ? (
                            <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                              Perdido
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">
                          {funnelLabel(prospect.funnelStage)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-secondary-600">{formatDate(prospect.collectedAtUtc)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {leadsOnly || prospect.actions.canEdit ? (
                          <EditIconLink
                            to={
                              leadsOnly
                                ? `/sales/leads/${prospect.id}/edit`
                                : `/sales/prospects/${prospect.id}/edit`
                            }
                            label={
                              leadsOnly
                                ? prospect.actions.canEdit
                                  ? 'Editar lead'
                                  : 'Abrir lead'
                                : 'Editar prospect'
                            }
                          />
                        ) : null}
                        {canWrite && prospect.funnelStage !== 'Lost' ? (
                          <EnrichIconButton onClick={() => void handleEnrichOne(prospect.id)} />
                        ) : null}
                        {prospect.actions.canMarkLost ? (
                          <button
                            type="button"
                            className="rounded-lg border border-amber-300 px-2 py-1 text-xs text-amber-800"
                            onClick={() => void handleMarkLost(prospect.id)}
                          >
                            Perdido
                          </button>
                        ) : null}
                        {prospect.actions.canSoftDelete || prospect.actions.canPermanentDelete ? (
                          <DeleteIconButton
                            label={
                              prospect.actions.canPermanentDelete && !prospect.actions.canSoftDelete
                                ? 'Excluir definitivamente'
                                : 'Remover prospect'
                            }
                            onClick={() => void handleDeleteProspect(prospect)}
                          />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalCount > pageSize ? (
        <div className="erp-pagination">
          <span>
            {totalCount} registro(s) — página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              className="erp-btn-secondary disabled:opacity-40"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              className="erp-btn-secondary disabled:opacity-40"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              Próxima
            </button>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
